import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as dealValidationSelectors from '@makemydeal/dr-dash-store/selectors/dealValidationSelectors';
import * as dealershipsSelectors from '@makemydeal/dr-dash-store/selectors/dealershipsSelectors';
import * as retentionSelectors from '@makemydeal/dr-dash-store/selectors/retentionSelectors';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as firstPencilSelectors from '@makemydeal/dr-dash-store/selectors/firstPencilSelectors';
import { featureToggleSelectors, dealerSelectors } from '@makemydeal/dr-shared-store';

import { FINANCE } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';

// types
import type { AdditionalSelectors, Dealership } from '@makemydeal/dr-activities-change-vehicle';
import { getOpenDealSelectors } from '../open-deal-dashboard/config';

// Finance only for the launch of F291824. Lease is hidden on the Change Vehicle AVO surface until lease is fully wired.
// Re-enabling is not just re-adding LEASE: this surface deliberately skips the dashboard's lease sub-toggle
// (isAIVehicleRecommendationEnabledForLease), so whether the re-enable should honor it is a Product call.
export const isAiAltVehicleOptionsDealEligible = (state: StateTree): boolean => {
    const offerType = offerReduxSelectors.getCurrentOfferType(state);

    return offerType === FINANCE;
};

// AVO feature active. Gates the whole feature — including "Add New Scenario", which stays available even when
// the deal isn't priceable (e.g. cash). Manager-View-only (non-MV hosts pin it false). At toggle cleanup drop
// only the F291824 term.
const mvIsAiAltVehicleOptionsActive = (state: StateTree): boolean =>
    featureToggleSelectors.isAiVehicleRecommendationsChangeVehicleEnabled(state) &&
    featureToggleSelectors.isAIVehicleRecommendationEnabled(state) &&
    dealerSelectors.isDealerAISearchEnabled(state);

// Section shows only when the feature is active AND the deal is eligible. Assigned to the additional selector
// below and read off that same object field by BOTH the render gate (the activity) and the screen-open fetch
// gate (middleware.ts), so the two can't drift apart.
const mvIsAiAltVehicleOptionsSectionVisible = (state: StateTree): boolean =>
    mvIsAiAltVehicleOptionsActive(state) && isAiAltVehicleOptionsDealEligible(state);

// Add future conditions here with &&.
const checkCanCreateScenario = (state: StateTree): boolean => firstPencilSelectors.allowsScenarioCreation(state);

export const mvDashabordAdditionalSelectors: AdditionalSelectors = {
    getCommonOrgId: offerReduxSelectors.getCommonOrgId,
    getDealerName: dealerSelectors.getDealerName,
    getDealId: offerReduxSelectors.getDealXgId,
    getServicesBff: configSelectors.getServicesBff,
    getVehicleVin: vehicleSelectors.getVehicleVin,
    getVehicleStockNumber: vehicleSelectors.getVehicleStockNumber,
    getDealerships: dealershipsSelectors.getDealerships,
    isEnableCrossDealershipAIVehicleSearch: featureToggleSelectors.isEnableCrossDealershipAIVehicleSearchMV,
    isPromptOptimizationEnabled: featureToggleSelectors.enablePromptOptimization,
    isSimilarityBodyStyleRelaxationEnabled: featureToggleSelectors.isSimilarityBodyStyleRelaxationEnabled,
    showAllVehiclesIfNoBaseVehicle: () => true,
    isAgeOfInventorySearchEnabled: featureToggleSelectors.isAgeOfInventorySearchEnabledMV,
    getDealerDiscountsTotal: offerReduxSelectors.getOfferDealerDiscountsTotalAmount,
    getIncentivesForRetentionModal: retentionSelectors.getIncentivesForRetentionModal,
    getAccessoriesForRetentionModal: retentionSelectors.getAccessoriesForRetentionModal,
    getVppForRetentionModal: retentionSelectors.getVppForRetentionModal,
    getManuallyOverriddenTaxItems: retentionSelectors.getManuallyOverriddenTaxItems,
    getRetentionTaxContext: retentionSelectors.getRetentionTaxContext,
    // Type-only bridge: dash-types AIVehicleRecommendation is under-declared vs the lib's
    // AiAltVehicleOption and the StateTree param differs (lib vs dash) — runtime is identical.
    getAiVehicleRecommendations:
        deskingSelectors.getAiVehicleRecommendations as unknown as AdditionalSelectors['getAiVehicleRecommendations'],
    getAiVehicleRecommendationsLoading:
        deskingSelectors.getAiVehicleRecommendationsLoading as unknown as AdditionalSelectors['getAiVehicleRecommendationsLoading'],
    isAiAltVehicleOptionsActive: mvIsAiAltVehicleOptionsActive,
    // Kept separate from isAiAltVehicleOptionsActive so cash hides only the section, not "Add New Scenario".
    isAiAltVehicleOptionsSectionVisible: mvIsAiAltVehicleOptionsSectionVisible,
    canCreateScenario: checkCanCreateScenario
};

export const openDealAdditionalSelectors: AdditionalSelectors = {
    getCommonOrgId: dealValidationSelectors.getInCompleteDealCoOrgId,
    getDealerName: dealerSelectors.getDealerName,
    getServicesBff: configSelectors.getServicesBff,
    getInitialSearchParameters: (state: any) => {
        const selectors = getOpenDealSelectors();
        return selectors.getVehicleVin(state);
    },
    getInitialPageSize: () => {
        const selectors = getOpenDealSelectors();
        return selectors.getInitialPageSize();
    },
    getDealerships: (state: any): Dealership[] | undefined => {
        const isCrossDealershipAISearchEnabled = featureToggleSelectors.isEnableCrossDealershipAIVehicleSearchOD(state);
        if (!isCrossDealershipAISearchEnabled) {
            return undefined;
        }

        const selectors = getOpenDealSelectors();
        return selectors.getDealers(state);
    },
    isEnableCrossDealershipAIVehicleSearch: featureToggleSelectors.isEnableCrossDealershipAIVehicleSearchOD,
    isPromptOptimizationEnabled: featureToggleSelectors.enablePromptOptimization,
    getIncentivesForRetentionModal: retentionSelectors.getIncentivesForRetentionModal,
    getAccessoriesForRetentionModal: retentionSelectors.getAccessoriesForRetentionModal,
    getVppForRetentionModal: retentionSelectors.getVppForRetentionModal,
    getManuallyOverriddenTaxItems: retentionSelectors.getManuallyOverriddenTaxItems,
    getRetentionTaxContext: retentionSelectors.getRetentionTaxContext,
    // AVO is Manager View-only; the dash app's open-deal config pins these false (explicit host-scope, so
    // AVO can't leak in via the library's `?? false` fallback if that default ever changes).
    isAiAltVehicleOptionsActive: () => false,
    isAiAltVehicleOptionsSectionVisible: () => false,
    canCreateScenario: () => false
};
